/**
 * User-granted, time-boxed email access for support (D-16). Staff can always open a workspace in support view,
 * with Gmail/Outlook-derived content hidden (src/utils/supportView.ts); they see the email only while its owner
 * approved a request. Lightbase files a request; the user sees it in real time (Supabase realtime on
 * `support_access_requests`, plus `GET /user/support-access` on boot, on resume and for
 * `?support_access=1` push links) and allows or denies it. While a grant is active a small banner / Settings row
 * shows until when, with Revoke.
 *
 * Pure state handling here; the composable is src/composables/useSupportAccess.ts.
 */

export type SupportAccessStatus = 'pending' | 'approved' | 'denied' | 'expired' | 'revoked'

export interface SupportAccessRow {
    id: string
    status: SupportAccessStatus
    reason: string
    requested_by_name: string
    duration_minutes: number
    created_at: string
    request_expires_at: string | null
    granted_until: string | null
}

export interface SupportAccessState {
    pending: SupportAccessRow | null
    active_grant: SupportAccessRow | null
}

const STATUSES = new Set<SupportAccessStatus>(['pending', 'approved', 'denied', 'expired', 'revoked'])

export function emptySupportAccessState(): SupportAccessState {
    return { pending: null, active_grant: null }
}

function time(iso: string | null | undefined): number | null {
    if (!iso) return null
    const t = Date.parse(iso)
    return Number.isFinite(t) ? t : null
}

/** A row from the API or a realtime payload (which carries every column) → the client shape, or null. */
export function normalizeSupportAccessRow(raw: unknown): SupportAccessRow | null {
    if (!raw || typeof raw !== 'object') return null
    const r = raw as Record<string, unknown>
    if (typeof r.id !== 'string' || !STATUSES.has(r.status as SupportAccessStatus)) return null
    const str = (v: unknown) => (typeof v === 'string' ? v : '')
    const minutes = Number(r.duration_minutes)
    return {
        id: r.id,
        status: r.status as SupportAccessStatus,
        reason: str(r.reason),
        requested_by_name: str(r.requested_by_name),
        duration_minutes: Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes) : 60,
        created_at: str(r.created_at),
        request_expires_at: str(r.request_expires_at) || null,
        granted_until: str(r.granted_until) || null,
    }
}

export function isPendingLive(row: SupportAccessRow | null | undefined, now: number): boolean {
    if (!row || row.status !== 'pending') return false
    const expires = time(row.request_expires_at)
    return expires == null || expires > now
}

export function isGrantActive(row: SupportAccessRow | null | undefined, now: number): boolean {
    if (!row || row.status !== 'approved') return false
    const until = time(row.granted_until)
    return until != null && until > now
}

export function parseSupportAccessState(data: unknown): SupportAccessState {
    if (!data || typeof data !== 'object') return emptySupportAccessState()
    const d = data as Record<string, unknown>
    return {
        pending: normalizeSupportAccessRow(d.pending),
        active_grant: normalizeSupportAccessRow(d.active_grant),
    }
}

/**
 * Fold one row (a realtime INSERT/UPDATE, or a respond / revoke response) into the state. A newer pending
 * request replaces an older one; a row that leaves `pending` clears it; an approved row becomes the grant;
 * a revoked / expired row clears the grant it was.
 */
export function applySupportAccessRow(state: SupportAccessState, row: SupportAccessRow): SupportAccessState {
    let { pending, active_grant } = state
    if (row.status === 'pending') {
        pending = row
    } else if (pending?.id === row.id) {
        pending = null
    }
    if (row.status === 'approved') {
        active_grant = row
    } else if (active_grant?.id === row.id) {
        active_grant = null
    }
    return { pending, active_grant }
}

/** Drop what has lapsed by `now` (a request past its 15-minute window, a grant past `granted_until`). */
export function pruneSupportAccessState(state: SupportAccessState, now: number): SupportAccessState {
    const pending = isPendingLive(state.pending, now) ? state.pending : null
    const active_grant = isGrantActive(state.active_grant, now) ? state.active_grant : null
    if (pending === state.pending && active_grant === state.active_grant) return state
    return { pending, active_grant }
}

/** "30 minutes", "1 hour", "1 hour 30 minutes", "2 hours". */
export function formatDuration(minutes: number): string {
    const m = Math.max(1, Math.round(minutes))
    const h = Math.floor(m / 60)
    const rest = m % 60
    const hours = h ? `${h} hour${h === 1 ? '' : 's'}` : ''
    const mins = rest ? `${rest} minute${rest === 1 ? '' : 's'}` : ''
    return [hours, mins].filter(Boolean).join(' ')
}

/** "Allow for 60 minutes" — the button keeps minutes, the body uses formatDuration. */
export function allowButtonLabel(minutes: number): string {
    return `Allow for ${Math.max(1, Math.round(minutes))} minutes`
}

/** "3:45 PM" (today) or "Oct 18, 3:45 PM". */
export function formatGrantUntil(iso: string | null, now: number, locale?: string): string {
    const t = time(iso)
    if (t == null) return ''
    const d = new Date(t)
    const sameDay = new Date(now).toDateString() === d.toDateString()
    const clock = d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })
    if (sameDay) return clock
    return `${d.toLocaleDateString(locale, { month: 'short', day: 'numeric' })}, ${clock}`
}
